import { CommonModule } from '@angular/common';
import {
    Component,
    Input,
    forwardRef,
    ChangeDetectionStrategy
} from '@angular/core';
import {
    AbstractControl,
    ControlValueAccessor,
    FormArray,
    FormBuilder,
    FormGroup,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    ReactiveFormsModule,
    ValidationErrors,
    Validator
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { TranslatePipe } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

export type ExternalReference = {
    serviceName: string;
    serviceValue: string;
};

@Component({
    selector: 'app-reference-ids',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatIconModule,
        MatSelectModule,
        TranslatePipe
    ],
    templateUrl: './reference-ids.component.html',
    styleUrls: ['./reference-ids.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ReferenceIdsComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => ReferenceIdsComponent),
            multi: true
        }
    ]
})
export class ReferenceIdsComponent implements ControlValueAccessor, Validator {
    @Input() serviceNames: string[] = [];

    form: FormGroup;
    isDisabled = false;

    private sub?: Subscription;

    private onChange: (value: ExternalReference[]) => void = () => {};
    private onTouched: () => void = () => {};

    constructor(private fb: FormBuilder) {
        this.form = this.fb.group({
            refs: this.fb.array<FormGroup>([])
        });

        this.sub = this.refsFA.valueChanges.subscribe(() => {
            this.onChange(this.getCleanValue());
            this.onTouched();
        });
    }

    get refsFA(): FormArray<FormGroup> {
        return this.form.get('refs') as FormArray<FormGroup>;
    }

    get rows(): FormGroup[] {
        return this.refsFA.controls as FormGroup[];
    }

    registerOnChange(fn: (value: ExternalReference[]) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    writeValue(value: ExternalReference[] | null): void {
        this.refsFA.clear({ emitEvent: false });
        const incoming = Array.isArray(value) ? value : [];
        const cleaned = incoming
            .map(v => ({
                serviceName: String(v?.serviceName ?? '').trim(),
                serviceValue: String(v?.serviceValue ?? '').trim()
            }))
            .filter(v => v.serviceName !== '' || v.serviceValue !== '');

        if (cleaned.length) {
            cleaned.forEach(v =>
                this.refsFA.push(this.createRow(v), { emitEvent: false })
            );
        }

        this.form.updateValueAndValidity({ emitEvent: false });
    }

    setDisabledState(isDisabled: boolean): void {
        this.isDisabled = isDisabled;
        if (isDisabled) this.form.disable({ emitEvent: false });
        else this.form.enable({ emitEvent: false });
    }

    validate(_: AbstractControl): ValidationErrors | null {
        return null;
    }

    addRow(): void {
        this.refsFA.push(this.createRow());
        this.onTouched();
        this.onChange(this.getCleanValue());
    }

    removeRow(index: number): void {
        this.refsFA.removeAt(index);
        this.onTouched();
        this.onChange(this.getCleanValue());
        this.form.updateValueAndValidity();
    }

    private createRow(v?: ExternalReference): FormGroup {
        return this.fb.group({
            serviceName: this.fb.control(v?.serviceName ?? ''),
            serviceValue: this.fb.control(v?.serviceValue ?? '')
        });
    }

    private getCleanValue(): ExternalReference[] {
        const arr = (this.refsFA.value as any[]) ?? [];
        return arr
            .map(v => ({
                serviceName: String(v?.serviceName ?? '').trim(),
                serviceValue: String(v?.serviceValue ?? '').trim()
            }))
            .filter(v => v.serviceName !== '' || v.serviceValue !== '');
    }
}
